/* eslint-disable no-use-before-define */
import React, { useEffect } from 'react'
import { useLocation, matchPath } from 'react-router-dom'
import PerfectScrollbar from 'react-perfect-scrollbar'
import PropTypes from 'prop-types'
import {
  Box,
  Divider,
  Drawer,
  Hidden,
  IconButton,
  List,
  ListSubheader,
  Tooltip,
  makeStyles,
} from '@material-ui/core'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import ReceiptIcon from '@material-ui/icons/ReceiptOutlined'
import PaymentOutlinedIcon from '@material-ui/icons/PaymentOutlined'
import {
  // Briefcase as BriefcaseIcon,
  Calendar as CalendarIcon,
  DollarSign as DollarSignIcon,
  Folder as FolderIcon,
  ShoppingCart as ShoppingCartIcon,
  Lock as LockIcon,
  UserPlus as UserPlusIcon,
  Home as HomeIcon,
  Settings as SettingsIcon,
  // Trello as TrelloIcon,
  User as UserIcon,
  Users as UsersIcon,
  Clipboard as ClipboardIcon,
  Package as PackageIcon,
  Tag as TagIcon,
} from 'react-feather'
import AccountBalanceWalletOutlinedIcon from '@material-ui/icons/AccountBalanceWalletOutlined'
import AccountBalanceOutlinedIcon from '@material-ui/icons/AccountBalanceOutlined'
import EventAvailableOutlinedIcon from '@material-ui/icons/EventAvailableOutlined'
import LocalAtmOutlinedIcon from '@material-ui/icons/LocalAtmOutlined'
import AccessibilityNewOutlinedIcon from '@material-ui/icons/AccessibilityNewOutlined'
import GroupAddOutlinedIcon from '@material-ui/icons/GroupAddOutlined'
import EqualizerOutlinedIcon from '@material-ui/icons/EqualizerOutlined'
import ManageAccountsOutlinedIcon from '@material-ui/icons/SupervisorAccountOutlined'
import SecurityOutlinedIcon from '@material-ui/icons/SecurityOutlined'
import MenuOutlinedIcon from '@material-ui/icons/MenuOutlined'
import SmsOutlinedIcon from '@material-ui/icons/SmsOutlined'
import EmailOutlinedIcon from '@material-ui/icons/EmailOutlined'
import PhoneAndroidOutlinedIcon from '@material-ui/icons/PhoneAndroidOutlined'
import LibraryBooksOutlinedIcon from '@material-ui/icons/LibraryBooksOutlined'
import StoreOutlinedIcon from '@material-ui/icons/StoreOutlined'
import { useTranslation } from 'react-i18next'
import NavItem from './NavItem'

const getSections = (t) => [
  {
    subheader: t('nav.management'),
    items: [
      { title: t('nav.roster'),     href: '/app/roster-v2',                 icon: EventAvailableOutlinedIcon },
      { title: t('nav.appointment'),href: '/app/appointment',               icon: CalendarIcon },
      { title: t('nav.customers'),  href: '/app/management/customers',      icon: UsersIcon },
      { title: t('nav.orders'),     href: '/app/management/orders',         icon: FolderIcon },
      { title: t('nav.products'),   href: '/app/management/products',       icon: ClipboardIcon },
      { title: t('nav.offers'),     href: '/app/management/offer',          icon: TagIcon },
      { title: t('nav.mobileSlips'), href: '/app/mobile-slip',                icon: ClipboardIcon },
      { title: t('nav.resources'),  href: '/app/management/resources',      icon: SettingsIcon },
      { title: t('nav.inventory'),  href: '/app/management/inventory',      icon: PackageIcon },
      { title: t('nav.payment'),    href: '/app/management/paymentmethod',  icon: PaymentOutlinedIcon },
      { title: t('nav.room'),       href: '/app/management/room',           icon: HomeIcon },
      { title: t('nav.employee'),   href: '/app/management/employee',       icon: GroupAddOutlinedIcon },
    ],
  },
  {
    subheader: t('nav.finance'),
    items: [
      { title: t('nav.income'),     href: '/app/finance/income',    icon: LocalAtmOutlinedIcon },
      { title: t('nav.expense'),    href: '/app/finance/expense',   icon: AccountBalanceOutlinedIcon },
      { title: t('nav.summary'),    href: '/app/finance/summary',   icon: ReceiptIcon },
      { title: t('nav.salary'),     href: '/app/finance/salary',    icon: AccountBalanceWalletOutlinedIcon },
      { title: t('nav.commission'), href: '/app/finance/commission',icon: AccessibilityNewOutlinedIcon },
      { title: t('nav.graph'),      href: '/app/finance/graph',     icon: EqualizerOutlinedIcon },
    ],
  },
  {
    subheader: t('nav.system'),
    items: [
      { title: t('nav.accounts'),        href: '/app/accounts',               icon: ManageAccountsOutlinedIcon },
      { title: t('nav.role'),            href: '/app/role',                   icon: SecurityOutlinedIcon },
      { title: t('nav.menu'),            href: '/app/system/menu',            icon: MenuOutlinedIcon },
      { title: t('nav.smsTemplate'),     href: '/app/system/sms-set',         icon: SmsOutlinedIcon },
      { title: t('nav.emailTemplate'),   href: '/app/system/email-template',  icon: EmailOutlinedIcon },
      { title: t('nav.smsNotifyPhones'), href: '/app/system/sms-notify-phone',icon: PhoneAndroidOutlinedIcon },
      { title: t('nav.dict'),            href: '/app/system/dict',            icon: LibraryBooksOutlinedIcon },
      { title: t('nav.store'),             href: '/app/system/store',           icon: StoreOutlinedIcon },
      { title: t('nav.bookingConfig'),     href: '/app/system/booking-config',  icon: LibraryBooksOutlinedIcon },
    ],
  },
]

function renderNavItems({ items, pathname, depth = 0, collapsed }) {
  return (
    <List disablePadding>
      {items.reduce(
        (acc, item) => reduceChildRoutes({ acc, item, pathname, depth, collapsed }),
        []
      )}
    </List>
  )
}

function reduceChildRoutes({ acc, pathname, item, depth, collapsed }) {
  const key = item.title + depth

  if (item.items) {
    const open = matchPath(pathname, {
      path: item.href,
      exact: false,
    })

    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        info={item.info}
        key={key}
        open={Boolean(open)}
        title={item.title}
        collapsed={collapsed}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items,
          collapsed,
        })}
      </NavItem>
    )
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        icon={item.icon}
        info={item.info}
        key={key}
        title={item.title}
        collapsed={collapsed}
      />
    )
  }

  return acc
}

const useStyles = makeStyles((theme) => ({
  mobileDrawer: {
    width: 256,
  },
  desktopDrawer: {
    top: 64,
    height: 'calc(100% - 64px)',
    overflowX: 'hidden',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  toggleBtn: {
    margin: theme.spacing(0.5),
  },
}))

const NavBar = ({ onMobileClose, openMobile }) => {
  const classes = useStyles()
  const location = useLocation()
  const { t } = useTranslation()
  const sections = getSections(t)

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  const content = (
    <Box height='100%' display='flex' flexDirection='column'>
      <PerfectScrollbar options={{ suppressScrollX: true }}>
        <Divider />
        <Box p={2}>
          {sections.map((section) => (
            <List
              key={section.subheader}
              subheader={
                <ListSubheader disableGutters disableSticky>
                  {section.subheader}
                </ListSubheader>
              }
            >
              {renderNavItems({
                items: section.items,
                pathname: location.pathname,
                collapsed: false,
              })}
            </List>
          ))}
        </Box>
      </PerfectScrollbar>
    </Box>
  )

  return (
    <Hidden lgUp>
      <Drawer
        anchor='left'
        classes={{ paper: classes.mobileDrawer }}
        onClose={onMobileClose}
        open={openMobile}
        variant='temporary'
      >
        {content}
      </Drawer>
    </Hidden>
  )
}

NavBar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
}

export default NavBar
